<script lang="ts" setup>
import { dir } from '@@/bits/current_dir'
import type { GeoMap, PostConnectionObject, PostMarkerObject } from '@@/bits/map'
import { ControlPosition, MARKER_SHAPE_POLY_COORDS } from '@@/bits/map'
import { WallWishGroupByTypes } from '@@/enums'
import OzBox, { OzBoxColors } from '@@/library/v4/components/OzBox.vue'
import type { OzMenuRowsRow } from '@@/library/v4/components/OzMenuRows.vue'
import OzMenuRows, { OzMenuRowsFocusStyle, OzMenuRowsSizePreset } from '@@/library/v4/components/OzMenuRows.vue'
import { useMapLocationSelectorStore } from '@@/pinia/map_location_selector_store'
import type { MapPopupOffsetStyle } from '@@/pinia/surface_map'
import { useSurfaceMapStore } from '@@/pinia/surface_map'
import { useSurfacePostConnectionStore } from '@@/pinia/surface_post_connection'
import { useSurfacePostsStore } from '@@/pinia/surface_posts'
import { useSurfaceWishArrangementStore } from '@@/pinia/surface_wish_arrangement'
import type { Cid, Id, PinDropSuggestion, PostColor } from '@@/types'
import SurfaceMapControlButtons from '@@/vuecomponents/SurfaceMapControlButtons.vue'
import SurfaceMapConnection from '@@/vuecomponents/surface_map_connection.vue'
import SurfaceMapGoogleMarker from '@@/vuecomponents/surface_map_google_marker.vue'
import { useSurfaceMapFormat } from '@@/vuecomposables/useSurfaceMapFormat'
import { storeToRefs } from 'pinia'
import type { ComputedRef } from 'vue'
import { computed, nextTick, onMounted, ref, watch, withDefaults } from 'vue'

const pinDropSuggestionRows: ComputedRef<OzMenuRowsRow<PinDropSuggestion>[]> = computed(() => {
  if (!pinDropSuggestions.value) return []
  return pinDropSuggestions.value.map((pinDropSuggestion) => ({
    text: pinDropSuggestion.locationName,
    eventPayload: pinDropSuggestion,
    testId: 'pinDropLocationSuggestion',
  }))
})

function handleSelectRow(_event: Event, eventPayload: PinDropSuggestion): void {
  pickLocationWithPinDropSuggestion(eventPayload)
}

const props = withDefaults(
  defineProps<{
    backgroundColor: string
    latitude: number
    longitude: number
    zoom: number
    markers: PostMarkerObject[]
    connections: PostConnectionObject[]
    themeId?: number
    isMobilePostListOverflowing?: boolean
    reactionIdsByPostId: Record<Id, Id[]>
    commentIdsByPostId: Record<Id, Id[]>
  }>(),
  {
    themeId: 100,
    isMobilePostListOverflowing: false,
    reactionIdsByPostId: () => ({}),
    commentIdsByPostId: () => ({}),
  },
)

const emit = defineEmits<{
  (name: 'click', e: Event): void
  (name: 'initialized', map: GeoMap): void
}>()

const MARKER_SHAPE: google.maps.MarkerShape = {
  coords: MARKER_SHAPE_POLY_COORDS,
  type: 'poly',
}

// Stores
const surfaceMapStore = useSurfaceMapStore()
const surfaceWishArrangementStore = useSurfaceWishArrangementStore()
const surfacePostConnectionStore = useSurfacePostConnectionStore()
const surfacePostsStore = useSurfacePostsStore()
const mapLocationSelectorStore = useMapLocationSelectorStore()

const {
  postListPosition,
  isPreviewPanelShown,
  offsetForMapPopup,
  pinDropSuggestions,
  pinDropLocation,
  postWithMarkerHighlightedCid,
  postWithPopupBeingOpenedCid,
} = storeToRefs(surfaceMapStore)

const { postBeingEditedCid } = storeToRefs(surfacePostsStore)

const { postToConnectFromId, postToDisconnectFromId } = storeToRefs(surfacePostConnectionStore)
const { completeDisconnection } = surfacePostConnectionStore

const { pickLocationWithPinDropSuggestion } = mapLocationSelectorStore

// Element Refs
const rootEl = ref<HTMLElement | null>(null)
const control = ref<InstanceType<typeof SurfaceMapControlButtons>>()

// #region POPUP LOGIC
/**
 * Clicking outside an editing popup should not close the popup
 * What will happen is that the post will revert to view mode first
 * The next click will close the popup
 */
const shouldClosePopup = computed(() => !postBeingEditedCid.value)

const isAPopupOpen = computed(() => {
  return (
    !!postWithPopupBeingOpenedCid.value ||
    !!(surfaceMapStore.pinDropSuggestions && surfaceMapStore.pinDropSuggestions.length > 0)
  )
})

const openMarkerPopup = (postCid: Cid): void => {
  surfaceMapStore.openPostPopup({ postCid })
}

const closeMarkerPopup = (postCid: Cid): void => {
  if (postWithPopupBeingOpenedCid.value === postCid) {
    surfaceMapStore.closePostPopup()
  }
}

watch(postBeingEditedCid, (val) => {
  if (val) {
    nextTick(() => {
      surfaceMapStore.openPostPopup({ postCid: val })
    })
  }
})

// #endregion

// Map setup using composable
const latitude = computed(() => props.latitude)
const longitude = computed(() => props.longitude)
const zoom = computed(() => props.zoom)
const themeId = computed(() => props.themeId)
const backgroundColor = computed(() => props.backgroundColor)
const markers = computed(() => props.markers)
const showMarkerOverlapCount = computed(() => surfaceMapStore.showMarkerOverlapCount)
const { mapManager, initializeMap, map, zoomIn, zoomOut, fitBounds, markerIcons } = useSurfaceMapFormat({
  rootEl,
  mapRenderer: 'google',
  latitude,
  longitude,
  zoom,
  themeId,
  backgroundColor,
  markers,
  isAPopupOpen,
  showMarkerOverlapCount: showMarkerOverlapCount.value,
  onMapClick: (e: Event & { placeId?: string; stop: () => void }) => {
    if (pinDropLocation.value) {
      surfaceMapStore.clearMapPinDropSuggestions()
      surfaceMapStore.stopPickingLocation()
    }
    if (shouldClosePopup.value) {
      surfaceMapStore.closePostPopup()
    }
    if (e.placeId) {
      // Ignore click on places of interest
      e.stop()
    }
    emit('click', e)
  },
  onMapInitialized: (map: GeoMap) => {
    // set up controls
    const mapControlLocation = dir() === 'rtl' ? ControlPosition.RIGHT_BOTTOM : ControlPosition.LEFT_BOTTOM
    map.controls[mapControlLocation].push(control.value?.$el as HTMLElement)

    emit('initialized', map)
  },
})

const mapConnections = ref<PostConnectionObject[]>([])
const mapMarkerRealWishStyle = ref<HTMLStyleElement | null>(null)

/* ---------------------- */
/* MAP STYLING            */
/* ---------------------- */
/**
 * Google Maps API can override the `style` attribute of the control.
 * To manually set the position, we have to use `class` with `!important`.
 */
const mapControlPositionClass = computed(() => {
  const isShowingGroupHeader = surfaceWishArrangementStore.wishGroupBy !== WallWishGroupByTypes.None
  let cls: string
  if (!isPreviewPanelShown.value) {
    // Default position: 20px from the bottom
    cls = '!bottom-[20px]'
  } else if (props.isMobilePostListOverflowing) {
    // Post list is opened: + post list height = +100px
    // Additional 24px if group header is shown
    cls = isShowingGroupHeader ? '!bottom-[144px]' : '!bottom-[120px]'
  } else {
    // Post list overflows (scrollbar shows): + scrollbar height is 14px - initial bottom margin 8px = +6px
    // Additional 24px if group header is shown
    cls = isShowingGroupHeader ? '!bottom-[150px]' : '!bottom-[126px]'
  }

  return cls
})

const getFillColor = (color?: PostColor): string => {
  const fill = markerIcons.value.fill
  const fillColor = fill[color || 'default']
  return fillColor
}

const updateMapMarkRealWishStyle = (updatedOffset: MapPopupOffsetStyle) => {
  mapMarkerRealWishStyle.value!.innerHTML = `
    .real-wish, .map-wish {
        max-height: calc(100vh - ${updatedOffset.top + updatedOffset.bottom}px);
    }
    .map-wish-with-stalker-bar {
      max-height: calc(100vh - ${updatedOffset.top + updatedOffset.bottom + 38}px);
    }
    `
}

/* ---------------------- */
/* PROP WATCHERS          */
/* ---------------------- */
watch(
  () => props.connections,
  (updatedConnections) => {
    mapConnections.value = updatedConnections
  },
  {
    immediate: true,
  },
)

watch(offsetForMapPopup, (updatedOffset) => {
  if (!mapMarkerRealWishStyle.value) return
  updateMapMarkRealWishStyle(updatedOffset)
})

/* ---------------------- */
/* ON MOUNTED             */
/* ---------------------- */
onMounted(() => {
  if (!rootEl.value) return

  initializeMap()

  nextTick(() => {
    mapMarkerRealWishStyle.value = document.createElement('style')
    updateMapMarkRealWishStyle(offsetForMapPopup.value)
    document.head.appendChild(mapMarkerRealWishStyle.value)
  })
})
</script>

<template>
  <div id="map-wrapper" ref="rootEl" class="surface-map-google" data-testid="surfaceMapGoogle">
    <div id="map" :style="{ 'background-color': backgroundColor }" />
    <div :dir="dir()">
      <SurfaceMapControlButtons
        ref="control"
        :class="{
          // We want to override Google's positioning of the control component.
          '!z-surface-map-control': true,
          [mapControlPositionClass]: postListPosition === 'bottom',
          'slide-leave-active': postListPosition === 'bottom' && !isPreviewPanelShown,
          'slide-enter-active': postListPosition === 'bottom' && isPreviewPanelShown,
          'ms-4': true,
        }"
        :orientation="postListPosition === 'bottom' ? 'vertical' : 'horizontal'"
        @reset-viewport="fitBounds"
        @zoom-out="zoomOut"
        @zoom-in="zoomIn"
      />
    </div>
    <div>
      <!-- N.B. surface-map-connection does not render any DOM elements, therefore we put them in all
        in a parent div so that Vue will not throw an error for using insertBefore
        to add new node to DOM before nonexistent node.
      -->
      <surface-map-connection
        v-for="connection in mapConnections"
        :key="'connection' + connection.id"
        :map="map"
        :from="connection.from"
        :to="connection.to"
        :label="connection.label"
        :label-count="connection.labelCount"
        :can-delete="
          postToDisconnectFromId === connection.from_wish_id || postToDisconnectFromId === connection.to_wish_id
        "
        :theme-id="themeId"
        @delete="completeDisconnection({ connectionId: connection.id })"
      />
    </div>
    <div>
      <!-- N.B. surface-map-google-marker contains DOM element that will be taken away,
        therefore we put them in all in a parent div so that Vue will not throw an error
        for using insertBefore to add new node to DOM before nonexistent node.
      -->
      <surface-map-google-marker
        v-for="marker in markers"
        :key="'marker' + marker.id"
        :map="map"
        :map-manager="mapManager"
        :stroke-color="markerIcons.stroke"
        :fill-color="getFillColor(marker.post.color)"
        :marker-shape="MARKER_SHAPE"
        :position="marker.position"
        :post="marker.post"
        :post-reaction-ids="reactionIdsByPostId[marker.post.id]"
        :post-comment-ids="commentIdsByPostId[marker.post.id]"
        :z-index="marker.zIndex"
        :highlighted="marker.post.cid === postWithMarkerHighlightedCid"
        :opened="
          (postWithPopupBeingOpenedCid === marker.id || postToConnectFromId === marker.post.id) &&
          !postToDisconnectFromId
        "
        :post-being-edited-cid="postBeingEditedCid"
        @open-marker-popup="openMarkerPopup"
        @close-marker-popup="closeMarkerPopup"
        @reset-viewport="fitBounds"
      />
      <!-- Marker that appears when dragging and dropping a pin -->
      <surface-map-google-marker
        v-if="pinDropSuggestions"
        :map="map"
        :map-manager="mapManager"
        :stroke-color="markerIcons.stroke"
        :fill-color="getFillColor()"
        :marker-shape="MARKER_SHAPE"
        :position="pinDropLocation"
        :allowing-grouping="false"
        :opened="true"
      >
        <OzBox
          class="w-[304px] shadow-elevation-3"
          :color="OzBoxColors.PrimaryBlur"
          :radius="24"
          data-testid="pinDropLocationSuggestionsMenu"
        >
          <header
            class="text-body font-semibold font-sans text-center pt-4 pb-3 px-2 text-dark-text-100 dark:text-light-text-100"
          >
            {{ __('What are you pinning?') }}
          </header>
          <OzMenuRows
            :rows="pinDropSuggestionRows"
            :focus-style="OzMenuRowsFocusStyle.Bubble100"
            :size-preset="OzMenuRowsSizePreset.H44px"
            @select-row="(_event, eventPayload) => handleSelectRow(_event, eventPayload)"
          />
        </OzBox>
      </surface-map-google-marker>
    </div>
  </div>
</template>

<style lang="scss">
.surface-map-google {
  width: 100%;
  flex-grow: 1;
  > div {
    background-color: transparent !important;
  }
}
[data-color-scheme='light'],
[data-color-scheme='dark'] {
  .wish .wish-selection {
    border-radius: 12px;
  }
}
</style>

<style lang="scss">
@import '@assets/stylesheets/3/modules/all';

.slide-enter-active {
  transition: bottom 0.25s cubic-bezier(0, 0, 0.2, 1); // https://material.io/design/motion/speed.html#easing
}
.slide-leave-active {
  transition: bottom 0.15s cubic-bezier(0.4, 0, 1, 1); // https://material.io/design/motion/speed.html#easing
}

.suggestion:not(:last-child) {
  border-bottom: 1px solid $border-color;
}
.map-control {
  display: flex;
  margin: 8px 12px;
  transform: none;
  transition: transform 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  @include respondToPhone {
    flex-direction: column;
    &.preview-shown {
      transform: translateY(-74px);
    }
  }
}
.map-button-group {
  display: flex;
  cursor: pointer;
  transition: all 0.1s cubic-bezier(0.4, 0, 0.2, 1); // Material ease https://material.io/design/motion/speed.html#easing
  border-radius: 8px;
  background-color: white;
  padding: 0;
  overflow: hidden;
  margin: 4px;
  @include shadow-z1;
  @include hover {
    @include shadow-z2;
  }
  @include respondToPhone {
    flex-direction: column;
    width: 32px;
  }

  > .map-button-inverse {
    transition: none;
    margin: 0px;
    border-radius: 0;
    box-shadow: none !important;
  }
  > .map-button-inverse:not(:last-of-type) {
    border-right: 1px solid $border-color;
  }
}

.map-button-inverse {
  transition: all 0.1s cubic-bezier(0.4, 0, 0.2, 1); // Material ease https://material.io/design/motion/speed.html#easing
  width: 32px;
  height: 32px;
  padding: 4px;
  margin: 4px;
  border-radius: 8px;
  background-color: white;
  color: $caption-text-color;
  border: none;
  @include shadow-z1;
  box-sizing: border-box;
  i {
    font-size: 18px !important;
  }
  @include hover {
    @include shadow-z2;
    background-color: darken(white, 5%);
    color: $caption-text-color;
  }

  &:active {
    @include shadow-z1;
    background-color: darken(white, 10%);
    color: $caption-text-color;
  }

  &:visited {
    color: $caption-text-color;
  }
}
</style>
